import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  IconButton,
  Drawer,
  Stack,
  useMediaQuery,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LoginIcon from "@mui/icons-material/Login";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { useTheme, alpha } from "@mui/material/styles";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const BRAND = "#F9735B";
const BRAND_DARK = "#DC5F49";
const INK = "#0F172A";
const BORDER = "#E2E8F0";

const PublicHeader = ({ variant = "light" }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const darkHero = variant === "darkHero";
  const navTextColor = darkHero ? "rgba(255,255,255,0.82)" : INK;
  const subtleBorder = darkHero ? "rgba(255,255,255,0.18)" : BORDER;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <MotionBox
        component="header"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          py: { xs: 1.25, md: 1.5 },
          transition: "all 0.25s ease",
          background: darkHero ? (scrolled ? "rgba(5,11,22,0.84)" : "transparent") : scrolled ? "rgba(255,255,255,0.86)" : "rgba(255,255,255,0.70)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: `1px solid ${darkHero ? (scrolled ? "rgba(255,255,255,0.12)" : "transparent") : scrolled ? alpha(BORDER, 0.95) : alpha(BORDER, 0.7)}`,
          boxShadow: scrolled ? (darkHero ? "0 12px 30px rgba(0,0,0,0.22)" : "0 6px 20px rgba(15,23,42,0.08)") : "none",
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: { xs: 56, md: 62 },
              gap: { xs: 1.5, md: 3 },
              px: 0,
              borderRadius: "14px",
              bgcolor: "transparent",
              border: "1px solid transparent",
              boxShadow: "none",
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
            }}
          >
            <MotionBox
              onClick={() => navigate("/")}
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1.2,
              }}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              transition={{ type: "spring", stiffness: 420, damping: 30 }}
            >
              <Box
                component="img"
                src="/assets/logo.png"
                alt="Menux"
                sx={{
                  height: { xs: 24, sm: 30 },
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </MotionBox>

            {!isMobile && (
              <Stack direction="row" spacing={1.4} alignItems="center">
                <Button
                  onClick={() => navigate("/restaurants")}
                  startIcon={<StorefrontOutlinedIcon sx={{ fontSize: 17 }} />}
                  sx={{
                    px: 1.55,
                    py: 1.05,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontSize: "0.84rem",
                    fontWeight: 700,
                    fontFamily: '"exo", "Inter", sans-serif',
                    color: navTextColor,
                    border: `1px solid ${darkHero ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.08)"}`,
                    bgcolor: darkHero ? "rgba(255,255,255,0.055)" : "rgba(248,250,252,0.86)",
                    "& .MuiButton-startIcon": {
                      mr: 0.75,
                      color: darkHero ? "#F9735B" : BRAND,
                    },
                    "&:hover": {
                      bgcolor: darkHero ? "rgba(249,115,91,0.12)" : "#FFF7F5",
                      borderColor: darkHero ? "rgba(249,115,91,0.32)" : "rgba(249,115,91,0.28)",
                    },
                  }}
                >
                  Restaurants
                </Button>
                <Button
                  onClick={() => navigate("/company-registration")}
                  startIcon={<RocketLaunchIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    px: 2.4,
                    py: 1.15,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontSize: "0.86rem",
                    fontWeight: 700,
                    fontFamily: '"exo", "Inter", sans-serif',
                    color: "#fff",
                    bgcolor: BRAND,
                    boxShadow: darkHero ? "0 12px 30px rgba(249,115,91,0.30)" : "0 8px 22px rgba(249,115,91,0.22)",
                    "&:hover": {
                      bgcolor: BRAND_DARK,
                      boxShadow: "0 10px 26px rgba(249,115,91,0.3)",
                    },
                  }}
                >
                  Register Restaurant
                </Button>

                <Button
                  onClick={() => navigate("/login")}
                  startIcon={<LoginIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    px: 2.2,
                    py: 1.15,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontSize: "0.86rem",
                    fontWeight: 700,
                    fontFamily: '"exo", "Inter", sans-serif',
                    color: navTextColor,
                    border: `1px solid ${subtleBorder}`,
                    bgcolor: darkHero ? "rgba(255,255,255,0.08)" : "#fff",
                    "&:hover": {
                      bgcolor: darkHero ? "rgba(255,255,255,0.14)" : "#F8FAFC",
                      borderColor: darkHero ? "rgba(255,255,255,0.34)" : "#CBD5E1",
                    },
                  }}
                >
                  Sign In
                </Button>
              </Stack>
            )}

            {isMobile && (
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{
                  width: 40,
                  height: 40,
                  color: navTextColor,
                  bgcolor: darkHero ? "rgba(255,255,255,0.10)" : "#fff",
                  border: `1px solid ${subtleBorder}`,
                  boxShadow: darkHero ? "none" : "0 5px 14px rgba(15,23,42,0.08)",
                  "&:hover": {
                    bgcolor: darkHero ? "rgba(255,255,255,0.16)" : "#fff",
                    borderColor: darkHero ? "rgba(255,255,255,0.34)" : "#CBD5E1",
                  },
                }}
              >
                <MenuIcon sx={{ fontSize: 22 }} />
              </IconButton>
            )}
          </Box>
        </Container>
      </MotionBox>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            maxWidth: "88vw",
            background: "#fff",
            boxShadow: "-8px 0 26px rgba(15,23,42,0.12)",
          },
        }}
      >
        <Box sx={{ p: 2.4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: INK, fontFamily: '"exo", "Inter", sans-serif' }}>
            Menu
          </Typography>
          <IconButton
            onClick={() => setDrawerOpen(false)}
            size="small"
            sx={{
              border: `1px solid ${BORDER}`,
              "&:hover": { bgcolor: "#F8FAFC" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: BORDER }} />

        <Box sx={{ p: 2.4 }}>
          <Stack spacing={1.3}>
            <Button
              fullWidth
              onClick={() => {
                navigate("/company-registration");
                setDrawerOpen(false);
              }}
              startIcon={<RocketLaunchIcon sx={{ fontSize: 18 }} />}
              sx={{
                justifyContent: "flex-start",
                px: 2,
                py: 1.3,
                borderRadius: "10px",
                textTransform: "none",
                fontSize: "0.9rem",
                fontWeight: 700,
                fontFamily: '"exo", "Inter", sans-serif',
                color: "#fff",
                bgcolor: BRAND,
                "&:hover": { bgcolor: BRAND_DARK },
              }}
            >
              Register Restaurant
            </Button>

            <Button
              fullWidth
              onClick={() => {
                navigate("/login");
                setDrawerOpen(false);
              }}
              startIcon={<LoginIcon sx={{ fontSize: 18 }} />}
              sx={{
                justifyContent: "flex-start",
                px: 2,
                py: 1.3,
                borderRadius: "10px",
                textTransform: "none",
                fontSize: "0.9rem",
                fontWeight: 700,
                fontFamily: '"exo", "Inter", sans-serif',
                color: INK,
                border: `1px solid ${BORDER}`,
                bgcolor: "#fff",
                "&:hover": { bgcolor: "#F8FAFC" },
              }}
            >
              Sign In
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};

export default PublicHeader;
